'use client';

import { useState, useEffect } from 'react';
import ServiceCard from './ServiceCard';

import Icon from '@/components/ui/AppIcon';
import WhatsAppContactButton from '@/components/common/WhatsAppContactButton';
import { trackEvent } from '@/lib/analytics';

interface Service {
  id: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  features: string[];
  responseTime: string;
  category: string;
}

interface ChecklistItem {
  id: string;
  task: string;
  priority: 'high' | 'medium' | 'low';
}

export default function ServicesInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [activeSeason, setActiveSeason] = useState<string>('winter');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = [
  { id: 'all', name: 'All Services', icon: 'Squares2X2Icon' },
  { id: 'hvac-filters', name: 'HVAC Filter Supply', icon: 'FireIcon' },
  { id: 'plumbing', name: 'Plumbing', icon: 'WrenchScrewdriverIcon' },
  { id: 'electrical', name: 'Electrical', icon: 'BoltIcon' },
  { id: 'general', name: 'General Maintenance', icon: 'BuildingOfficeIcon' },
  { id: 'staffing', name: 'Temporary Staffing', icon: 'UsersIcon' },
  { id: 'emergency', name: 'Emergency Services', icon: 'ExclamationTriangleIcon' }];


  const services: Service[] = [
  {
    id: '1',
    title: 'Temporary Shift Coverage',
    description: 'Certified 4th and 5th class power engineers available for temporary placements and emergency coverage. Professional maintenance staff for vacations, sick leave, or peak demand.',
    image: "https://images.pexels.com/photos/5691546/pexels-photo-5691546.jpeg",
    alt: 'Power engineer in safety gear checking pressure gauges on industrial boiler system',
    features: [
    '4th & 5th class power engineers',
    'Licensed professionals',
    'Same-day coverage availability',
    'Flexible scheduling',
    'Emergency coverage',
    'Seamless team integration'],

    responseTime: 'Same Day',
    category: 'staffing'
  },
  {
    id: '2',
    title: 'Parkade Power Sweeping',
    description: 'Professional parkade and parking lot cleaning services to maintain clean, safe environments for tenants and visitors.',
    image: "https://images.pexels.com/photos/8853502/pexels-photo-8853502.jpeg",
    alt: 'Blue ride-on floor sweeper machine cleaning underground concrete parkade',
    features: [
    'Power sweeping services',
    'Debris removal',
    'Regular scheduling available',
    'Post-construction cleanup',
    'Environmentally friendly',
    'Professional equipment'],

    responseTime: '24-48 hours',
    category: 'general'
  },
  {
    id: '3',
    title: 'Window Cleaning - Highrise & Lowrise',
    description: 'Professional exterior and interior window cleaning for highrise and lowrise buildings. Our certified team handles everything from ground-level to high-altitude window cleaning.',
    image: "https://images.pexels.com/photos/5725047/pexels-photo-5725047.jpeg",
    alt: 'Two window cleaners in suspended metal gondola cleaning glass building exterior with squeegees',
    features: [
    'Highrise exterior cleaning',
    'Lowrise building cleaning',
    'Rope access certified',
    'Interior window cleaning',
    'Streak-free results',
    'Safety certified team'],

    responseTime: '2-3 days',
    category: 'general'
  },
  {
    id: '4',
    title: 'Janitorial Services',
    description: 'Comprehensive commercial janitorial services including daily cleaning, floor care, interior window cleaning, and waste management for professional environments.',
    image: "https://images.pexels.com/photos/4107278/pexels-photo-4107278.jpeg",
    alt: 'Professional cleaner wiping down window sill in commercial building',
    features: [
    'Daily cleaning services',
    'Interior window cleaning',
    'Floor care & maintenance',
    'Restroom sanitization',
    'Waste management',
    'Professional staff'],

    responseTime: 'Daily/Weekly',
    category: 'general'
  },
  {
    id: '5',
    title: 'Painting, Carpentry & Drywall',
    description: 'Expert drywall repair, professional painting services, and skilled carpentry work for both interior and exterior commercial spaces.',
    image: "https://images.pexels.com/photos/8961277/pexels-photo-8961277.jpeg",
    alt: 'Construction worker installing and finishing drywall on commercial wall',
    features: [
    'Interior painting',
    'Exterior coating',
    'Drywall repair',
    'Carpentry work',
    'Surface preparation',
    'Quality finishes'],

    responseTime: '2-3 days',
    category: 'general'
  },
  {
    id: '6',
    title: 'Handyman Services',
    description: 'Versatile handyman services for all those small to medium repairs and improvements your property needs.',
    image: "https://images.pexels.com/photos/5691608/pexels-photo-5691608.jpeg",
    alt: 'Handyman installing wall-mounted storage rack and shelving system',
    features: [
    'General repairs',
    'Fixture installation',
    'Minor renovations',
    'Quick response',
    'Multi-skilled technicians',
    'Reliable service'],

    responseTime: '1-2 days',
    category: 'general'
  },
  {
    id: '7',
    title: 'Lighting Supply & Installation',
    description: 'Professional lighting supply, installation, and maintenance services for commercial properties.',
    image: "https://images.pexels.com/photos/8092342/pexels-photo-8092342.jpeg",
    alt: 'Electrician installing recessed LED pot light in commercial office ceiling',
    features: [
    'LED lighting solutions',
    'Pot light installation',
    'Supply and delivery',
    'Energy-efficient options',
    'Maintenance and repairs',
    'Emergency lighting'],

    responseTime: '2-4 hours',
    category: 'electrical'
  },
  {
    id: '8',
    title: 'Electrical Services',
    description: 'Licensed electrical contractors providing safe, reliable electrical solutions for commercial properties.',
    image: "https://images.unsplash.com/photo-1615774925655-a0e97fc85c14",
    alt: 'Certified electrician in safety gear working on commercial electrical panel with testing equipment in industrial facility',
    features: [
    'Electrical system inspections',
    'Panel upgrades and repairs',
    'Installation and maintenance',
    'Emergency power systems',
    'Code compliance updates',
    'Energy-efficient retrofits'],

    responseTime: '2-4 hours',
    category: 'electrical'
  },
  {
    id: '9',
    title: 'HVAC Filter Supply & Delivery',
    description: 'Complete HVAC filter supply and delivery solutions for commercial properties across the Lower Mainland.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d378a403-1765186850678.png",
    alt: 'Commercial HVAC air filter being replaced by technician in commercial building',
    features: [
    'Wide range of filter sizes',
    'Scheduled delivery services',
    'Filter replacement recommendations',
    'Bulk order discounts',
    'Emergency filter delivery',
    'Filter quality assessments'],

    responseTime: '24 hours',
    category: 'hvac-filters'
  },
  {
    id: '10',
    title: 'Commercial Plumbing Solutions',
    description: 'Expert plumbing services for commercial buildings, from routine maintenance to repairs.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_104e29039-1764692414441.png",
    alt: 'Skilled plumber in work uniform repairing commercial sink pipes with professional tools in modern office building',
    features: [
    'Drain cleaning and maintenance',
    'Pipe repair and replacement',
    'Water heater services',
    'Backflow prevention',
    'Fixture installation and repair',
    'Sewer line inspection and repair'],

    responseTime: '1-3 hours',
    category: 'plumbing'
  },
  {
    id: '11',
    title: 'General Building Maintenance',
    description: 'Comprehensive maintenance services to keep your commercial property in optimal condition year-round.',
    image: "https://images.unsplash.com/photo-1695045484706-0df43f5b5f1b",
    alt: 'Maintenance worker in safety vest performing routine building inspection with clipboard in modern commercial office lobby',
    features: [
    'Routine property inspections',
    'Door and window maintenance',
    'Flooring repairs',
    'Exterior maintenance',
    'Building systems care',
    'Preventative programs'],

    responseTime: '24-48 hours',
    category: 'general'
  },
  {
    id: '12',
    title: 'Preventative Maintenance Programs',
    description: 'Customized maintenance plans designed to prevent issues before they become costly problems.',
    image: "https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b",
    alt: 'Maintenance schedule calendar showing regular checkmarks and planned preventative maintenance inspections throughout the year',
    features: [
    'Scheduled inspections',
    'Equipment lifecycle management',
    'Detailed maintenance reports',
    'Priority service scheduling',
    'Cost-effective planning',
    'Extended equipment lifespan'],

    responseTime: 'Scheduled',
    category: 'general'
  }];


  const seasonalChecklists: {[key: string]: ChecklistItem[];} = {
    winter: [
    { id: 'w1', task: 'Inspect and service heating systems', priority: 'high' },
    { id: 'w2', task: 'Check roof for ice dam formation', priority: 'high' },
    { id: 'w3', task: 'Test emergency generators', priority: 'high' },
    { id: 'w4', task: 'Inspect and clear drainage systems', priority: 'medium' },
    { id: 'w5', task: 'Check building insulation and weatherstripping', priority: 'medium' },
    { id: 'w6', task: 'Service snow removal equipment', priority: 'medium' },
    { id: 'w7', task: 'Inspect parking lot lighting', priority: 'low' },
    { id: 'w8', task: 'Review emergency response procedures', priority: 'low' }],

    spring: [
    { id: 's1', task: 'System spring tune-up', priority: 'high' },
    { id: 's2', task: 'Inspect roof for winter damage', priority: 'high' },
    { id: 's3', task: 'Clean and inspect gutters and downspouts', priority: 'high' },
    { id: 's4', task: 'Test fire suppression systems', priority: 'medium' },
    { id: 's5', task: 'Inspect exterior building envelope', priority: 'medium' },
    { id: 's6', task: 'Service landscaping and irrigation', priority: 'medium' },
    { id: 's7', task: 'Power wash exterior surfaces', priority: 'low' },
    { id: 's8', task: 'Inspect parking lot and repair cracks', priority: 'low' }],

    summer: [
    { id: 'su1', task: 'Inspect and service cooling systems', priority: 'high' },
    { id: 'su2', task: 'Check roof membrane for damage', priority: 'high' },
    { id: 'su3', task: 'Test backup power systems', priority: 'medium' },
    { id: 'su4', task: 'Inspect and clean exterior lighting', priority: 'medium' },
    { id: 'su5', task: 'Service irrigation systems', priority: 'medium' },
    { id: 'su6', task: 'Inspect windows and doors for air leaks', priority: 'low' },
    { id: 'su7', task: 'Clean and maintain outdoor areas', priority: 'low' },
    { id: 'su8', task: 'Review summer energy usage', priority: 'low' }],

    fall: [
    { id: 'f1', task: 'Prepare heating systems for winter', priority: 'high' },
    { id: 'f2', task: 'Clean gutters and downspouts', priority: 'high' },
    { id: 'f3', task: 'Inspect roof before winter weather', priority: 'high' },
    { id: 'f4', task: 'Test emergency lighting systems', priority: 'medium' },
    { id: 'f5', task: 'Winterize irrigation systems', priority: 'medium' },
    { id: 'f6', task: 'Inspect building envelope for drafts', priority: 'medium' },
    { id: 'f7', task: 'Service snow removal equipment', priority: 'low' },
    { id: 'f8', task: 'Review winter emergency procedures', priority: 'low' }]

  };

  const filteredServices = activeCategory === 'all' ?
  services :
  services.filter((service) => service.category === activeCategory);

  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);

    // Track service category engagement
    trackEvent('service_category_view', {
      event_category: 'Services',
      event_label: categoryId,
      page_location: '/services'
    });
  };

  if (!isHydrated) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-12 bg-border rounded w-1/3"></div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) =>
          <div key={i} className="h-96 bg-border rounded-lg"></div>
          )}
        </div>
      </div>);

  }

  return (
    <div className="space-y-12">
      {/* Service Categories Filter */}
      <div className="bg-surface border border-border rounded-lg p-4">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) =>
          <button
            key={category.id}
            onClick={() => handleCategoryChange(category.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
            activeCategory === category.id ?
            'bg-primary text-primary-foreground shadow-md' :
            'bg-card text-text-secondary hover:bg-muted'}`
            }>

              <Icon name={category.icon as any} size={18} variant="outline" />
              {category.name}
            </button>
          )}
        </div>
      </div>

      {/* Temporary Shift Coverage Highlight Section */}
      <div className="bg-gradient-to-br from-primary to-secondary text-white rounded-xl p-8 shadow-xl border-4 border-warning">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-warning text-warning-foreground p-3 rounded-full">
              <Icon name="UsersIcon" size={32} variant="solid" />
            </div>
            <div>
              <h2 className="text-3xl font-bold">Temporary Shift Coverage</h2>
            </div>
          </div>
          <p className="text-lg mb-6 opacity-90">
            Never worry about staffing shortages again. Our professional maintenance staff provide seamless coverage for vacations, sick leave, or peak demand periods. Same-day availability with fully licensed and certified technicians across all trades.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="ClockIcon" size={24} variant="outline" />
                <h3 className="font-bold">Same-Day Response</h3>
              </div>
              <p className="text-sm opacity-90">Emergency coverage available within hours</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="ShieldCheckIcon" size={24} variant="solid" />
                <h3 className="font-bold">Fully Certified</h3>
              </div>
              <p className="text-sm opacity-90">Licensed professionals across all trades</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="CalendarDaysIcon" size={24} variant="outline" />
                <h3 className="font-bold">Flexible Duration</h3>
              </div>
              <p className="text-sm opacity-90">From single days to extended months</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button className="bg-white text-primary px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 font-semibold flex items-center gap-2">
              <Icon name="DocumentTextIcon" size={20} variant="outline" />
              Request Staffing Quote
            </button>
            <WhatsAppContactButton
              variant="cta"
              serviceName="Temporary Shift Coverage" />

          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-text-primary">
            {activeCategory === 'all' ? 'All Services' : categories.find((c) => c.id === activeCategory)?.name}
          </h2>
          <span className="text-sm text-text-secondary">
            {filteredServices.length} {filteredServices.length === 1 ? 'Service' : 'Services'}
          </span>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service) =>
          <ServiceCard key={service.id} {...service} />
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary text-primary-foreground rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold mb-3">Ready to Get Started?</h2>
        <p className="text-sm opacity-90 mb-6 max-w-2xl mx-auto">
          Contact us today for a free consultation and quote to discuss your property maintenance needs.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button className="bg-white text-primary px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 font-semibold flex items-center justify-center gap-2">
            <Icon name="DocumentTextIcon" size={20} variant="outline" />
            Request Free Quote
          </button>
          <WhatsAppContactButton variant="cta" />
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      <WhatsAppContactButton variant="floating" showLabel={false} className="fixed bottom-6 right-6 z-50 p-4" />
    </div>);

}